import { getDefaultVoiceForModel } from "./realtimeModels";
import type {
	Agent,
	AgentDeploymentRouting,
	AgentProvider,
	Company,
	SystemSettings,
} from "./types";

export function resolveElevenLabsProviderEnabled(
	settings?: Pick<SystemSettings, "elevenLabsProvider"> | null,
	company?: Pick<Company, "features"> | null,
): boolean {
	const override = company?.features?.elevenLabsProvider;
	return typeof override === "boolean"
		? override
		: settings?.elevenLabsProvider?.defaultEnabled === true;
}

export function getAgentDraftProvider(
	agent: Pick<Agent, "draftProvider" | "provider">,
	elevenLabsEnabled = true,
): AgentProvider {
	if (!elevenLabsEnabled) return "livekit";
	return agent.draftProvider ?? agent.provider ?? "livekit";
}

/** Old agents keep their existing provider until a channel publishes routing metadata. */
export function getAgentChannelRouting(
	agent: Pick<
		Agent,
		"provider" | "elevenlabs" | "stagingDeployment" | "liveDeployment"
	>,
	channel: "test" | "live",
	elevenLabsEnabled = true,
): AgentDeploymentRouting {
	if (!elevenLabsEnabled) return { provider: "livekit" };
	const deployment =
		channel === "test" ? agent.stagingDeployment : agent.liveDeployment;
	if (deployment?.routing) return deployment.routing;
	const provider = agent.provider ?? "livekit";
	if (provider === "elevenlabs" && agent.elevenlabs) {
		const { agentId, sipUris, workspaceId, credentialVersion } =
			agent.elevenlabs;
		return {
			provider,
			elevenlabs: {
				agentId,
				sipUri: sipUris[channel],
				...(workspaceId ? { workspaceId } : {}),
				...(credentialVersion ? { credentialVersion } : {}),
			},
		};
	}
	return { provider };
}

/** Changes only the draft. Existing bindings and channel summaries are preserved. */
export function selectAgentDraftProvider<T extends Agent>(
	agent: T,
	provider: AgentProvider,
): T {
	const previous = getAgentDraftProvider(agent);
	if (provider === previous) return agent;
	const preferences = {
		...agent.providerVoiceSettings,
		[previous]: {
			...(agent.voice ? { voice: agent.voice } : {}),
			...(agent.defaultLanguage ? { language: agent.defaultLanguage } : {}),
		},
	};
	const remembered = preferences[provider];
	return {
		...agent,
		draftProvider: provider,
		providerVoiceSettings: preferences,
		voice:
			remembered?.voice ??
			(provider === "livekit"
				? getDefaultVoiceForModel(
						agent.realtime?.provider,
						agent.realtime?.model,
					)
				: ""),
		defaultLanguage: remembered?.language ?? agent.defaultLanguage ?? "en",
	};
}
